import { ExerciseRecord, ExerciseSearchFilters, ExerciseSearchItem } from '../../api/exercises.interface';

import { ExerciseFormErrors, ExerciseFormValues, ExerciseSearchFormValues } from './exercise.types';

const FORM_FIELD_NAMES: Array<keyof ExerciseFormValues> = [
    'exercise',
    'tag',
    'level',
    'plan',
    'description',
    'step',
    'reps',
    'weight',
    'weightRange',
    'mat',
    'bootyBand',
    'videoUrl',
];

const REQUIRED_FORM_FIELD_NAMES: Array<keyof ExerciseFormValues> = [
    'exercise',
    'tag',
    'level',
    'plan',
    'description',
    'step',
    'reps',
    'weight',
    'weightRange',
    'mat',
    'bootyBand',
    'videoUrl',
];

const FIELD_LABELS: Record<keyof ExerciseFormValues, string> = {
    bootyBand: 'Booty band',
    description: 'Description',
    exercise: 'Exercise name',
    level: 'Level',
    mat: 'Mat required',
    plan: 'Plan',
    reps: 'Reps',
    step: 'Step',
    tag: 'Tag',
    videoUrl: 'High-res video URL',
    weight: 'Weight',
    weightRange: 'Weight range',
};

const isValidUrl = (value: string): boolean => {
    try {
        const parsedUrl = new URL(value);

        return ['http:', 'https:'].includes(parsedUrl.protocol);
    } catch (error) {
        return false;
    }
};

export const createEmptyExerciseFormValues = (): ExerciseFormValues => ({
    bootyBand: 'None',
    description: '',
    exercise: '',
    level: '',
    mat: 'No',
    plan: '',
    reps: '',
    step: '',
    tag: '',
    videoUrl: '',
    weight: '',
    weightRange: '',
});

export const createExerciseFormValues = (exercise?: ExerciseRecord): ExerciseFormValues => {
    if (!exercise) {
        return createEmptyExerciseFormValues();
    }

    return {
        bootyBand: exercise.bootyBand,
        description: exercise.description,
        exercise: exercise.exercise,
        level: exercise.level,
        mat: exercise.mat,
        plan: exercise.plan,
        reps: exercise.reps,
        step: exercise.step,
        tag: `${exercise.tag}`,
        videoUrl: exercise.videoUrl,
        weight: exercise.weight,
        weightRange: exercise.weightRange,
    };
};

export const createExerciseSearchFormValues = (filters?: ExerciseSearchFilters): ExerciseSearchFormValues => ({
    level: filters?.level || '',
    name: filters?.name || '',
    tag: filters?.tags?.length ? filters.tags.join(',') : filters?.tag !== undefined ? `${filters.tag}` : '',
});

export const createExercisePayload = (values: ExerciseFormValues): ExerciseRecord => ({
    bootyBand: values.bootyBand.trim(),
    description: values.description.trim(),
    exercise: values.exercise.trim(),
    level: values.level.trim(),
    mat: values.mat.trim(),
    plan: values.plan.trim(),
    reps: values.reps.trim(),
    step: values.step.trim(),
    tag: Number(values.tag.trim()),
    videoUrl: values.videoUrl.trim(),
    weight: values.weight.trim(),
    weightRange: values.weightRange.trim(),
});

export const validateExerciseForm = (values: ExerciseFormValues): ExerciseFormErrors => {
    const errors: ExerciseFormErrors = {};

    REQUIRED_FORM_FIELD_NAMES.forEach((fieldName) => {
        if (!values[fieldName].trim()) {
            errors[fieldName] = `${FIELD_LABELS[fieldName]} is required.`;
        }
    });

    if (values.tag.trim()) {
        const parsedTag = Number(values.tag);

        if (!Number.isInteger(parsedTag) || parsedTag < 0) {
            errors.tag = 'Tag must be a non-negative whole number.';
        }
    }

    if (values.videoUrl.trim() && !isValidUrl(values.videoUrl.trim())) {
        errors.videoUrl = 'Enter a valid URL.';
    }

    return errors;
};

export const parseExerciseSearchFilters = (
    values: ExerciseSearchFormValues,
): { error?: string; filters?: ExerciseSearchFilters } => {
    const filters: ExerciseSearchFilters = {};
    const trimmedName = values.name.trim();
    const trimmedLevel = values.level.trim();
    const trimmedTag = values.tag.trim();

    if (trimmedName) {
        filters.name = trimmedName;
    }

    if (trimmedLevel) {
        filters.level = trimmedLevel;
    }

    if (trimmedTag) {
        const parsedTags = trimmedTag.split(',').map((value) => value.trim());

        if (parsedTags.some((value) => !value)) {
            return { error: 'Tags must be comma-delimited non-negative whole numbers.' };
        }

        const normalizedTags = Array.from(new Set(parsedTags.map((value) => Number(value))));

        if (normalizedTags.some((value) => !Number.isInteger(value) || value < 0)) {
            return { error: parsedTags.length > 1 ? 'Tags must be comma-delimited non-negative whole numbers.' : 'Tag must be a non-negative whole number.' };
        }

        if (normalizedTags.length === 1) {
            filters.tag = normalizedTags[0];
        } else {
            filters.tags = normalizedTags;
        }
    }

    if (!filters.name && !filters.level && filters.tag === undefined && !filters.tags?.length) {
        return { error: 'Enter at least one search filter.' };
    }

    return { filters };
};

export const hasUnsavedExerciseChanges = (
    initialValues: ExerciseFormValues,
    currentValues: ExerciseFormValues,
): boolean => FORM_FIELD_NAMES.some((fieldName) => initialValues[fieldName] !== currentValues[fieldName]);

export const buildExerciseSearchSummary = (
    filters: ExerciseSearchFilters,
    resultCount: number,
): string => {
    if (filters.name) {
        return `Showing ${resultCount} result${resultCount === 1 ? '' : 's'} for "${filters.name}"`;
    }

    if (filters.tag !== undefined) {
        return `Showing ${resultCount} result${resultCount === 1 ? '' : 's'} for tag ${filters.tag}`;
    }

    if (filters.tags?.length) {
        return `Showing ${resultCount} result${resultCount === 1 ? '' : 's'} for tags ${filters.tags.join(', ')}`;
    }

    if (filters.level) {
        return `Showing ${resultCount} result${resultCount === 1 ? '' : 's'} for ${filters.level}`;
    }

    return `Showing ${resultCount} result${resultCount === 1 ? '' : 's'}`;
};

export const createOptionValuesWithCurrentValue = (
    values: string[],
    currentValue: string,
): string[] => {
    if (!currentValue || values.includes(currentValue)) {
        return values;
    }

    return [currentValue, ...values];
};

export const findDuplicateTagError = (message?: string): string | undefined => {
    if (!message) {
        return undefined;
    }

    return message.toLowerCase().includes('tag') ? message : undefined;
};

export const findExerciseByIndex = (
    items: ExerciseSearchItem[],
    index: number,
): ExerciseSearchItem | undefined => items.find((item) => item.index === index);